// Badge display components. Reads /api/users/me/badges and /api/badges/definitions.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const STATIC_DEFS = {
  pioniere: { label: "Pioniere", emoji: "🌱", description: "Il primo a raccontare una scuola.", tier: "base" },
  generoso: { label: "Generoso", emoji: "🎁", description: "Hai contribuito al catalogo libri & giochi.", tier: "base" },
  attento: { label: "Attento", emoji: "👁️", description: "Hai segnalato un contenuto fuori linee guida.", tier: "base" },
  guida: { label: "Guida", emoji: "🧭", description: "5+ contribuzioni accettate.", tier: "gold" },
  leader: { label: "Leader", emoji: "🏆", description: "10+ contribuzioni accettate.", tier: "gold" },
};

export function BadgeChip({ badge, size = "md" }) {
  const def = STATIC_DEFS[badge.badge_type] || {};
  const label = badge.label || def.label || badge.badge_type;
  const emoji = badge.emoji || def.emoji || "🏅";
  const isGold = def.tier === "gold";
  const cls = size === "sm" ? "text-[10px] px-1.5 py-0.5 gap-1" : "text-xs px-2 py-1 gap-1.5";
  const colors = isGold
    ? "bg-[#FCF1D8] border border-[#E6CB7E] text-[#7A5A1E]"
    : "bg-[#FFF8F0] border border-[#EFE3D2] text-[#1A362D]";
  return (
    <span
      title={badge.description || def.description}
      data-testid={`badge-chip-${badge.badge_type}`}
      className={`inline-flex items-center rounded-full font-ui font-medium ${colors} ${cls}`}
    >
      <span aria-hidden>{emoji}</span> {label}
    </span>
  );
}

export function MyBadges() {
  const [badges, setBadges] = useState([]);
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/users/me/badges").then((r) => r.data).catch(() => []),
      api.get("/badges/definitions").then((r) => r.data).catch(() => []),
    ]).then(([b, d]) => {
      setBadges(b);
      setDefs(d);
      setLoading(false);
    });
  }, []);

  if (loading) return null;

  const earnedSet = new Set(badges.map((b) => b.badge_type));

  return (
    <section data-testid="my-badges">
      <h2 className="font-display text-2xl text-[#1A362D] tracking-tight">I tuoi badge</h2>
      <p className="mt-1 font-ui text-sm text-[#5C6B64]">
        Riconoscimenti che racconti la tua partecipazione alla community.
      </p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {defs.map((d) => {
          const earned = earnedSet.has(d.slug);
          const meta = badges.find((b) => b.badge_type === d.slug);
          const isGold = STATIC_DEFS[d.slug]?.tier === "gold";
          return (
            <div
              key={d.slug}
              data-testid={`badge-card-${d.slug}`}
              className={`rounded-xl p-5 border transition-all ${
                earned
                  ? isGold
                    ? "bg-[#FCF1D8] border-[#E6CB7E] shadow-[0_4px_16px_-8px_rgba(166,124,30,0.35)]"
                    : "bg-[#FFF8F0] border-[#EFE3D2] shadow-[0_4px_16px_-8px_rgba(192,92,69,0.25)]"
                  : "bg-white border-[#E8E6E1] opacity-70"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`text-3xl ${earned ? "" : "grayscale opacity-60"}`} aria-hidden>{d.emoji}</span>
                <div>
                  <p className={`font-display text-lg ${isGold && earned ? "text-[#7A5A1E]" : "text-[#1A362D]"}`}>{d.label}</p>
                  {earned ? (
                    <p className={`text-[10px] font-ui uppercase tracking-wider ${isGold ? "text-[#A68420]" : "text-[#C05C45]"}`}>
                      Ottenuto · {new Date(meta.earned_at).toLocaleDateString("it-IT")}
                    </p>
                  ) : (
                    <p className="text-[10px] font-ui uppercase tracking-wider text-[#8A9691]">Da ottenere</p>
                  )}
                </div>
              </div>
              <p className="mt-3 font-ui text-xs text-[#5C6B64] leading-relaxed">{d.description}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
